/**
 * The route as a diagnosable thing.
 *
 * Thirteen stations in this fab can be handed a complaint and asked what is
 * wrong with them. This one asks a harder question, and it is the question a
 * yield engineer actually gets:
 *
 *     the product is out of specification and no tool is
 *
 * The candidate answers are not "which chamber" but "which KIND of cause":
 * something accumulating across revisits, a shared budget that has been spent,
 * a slow drift that no single measurement can see - or, and this is the one
 * that keeps the exercise honest, an ordinary tool fault that happens to have
 * been missed.
 *
 * WHY THE FOURTH CANDIDATE MATTERS
 *
 * A catalogue containing only route faults would teach "stop looking at the
 * station", which is exactly as wrong as "always look at the station". Most
 * yield excursions ARE one tool. The competency is telling the two apart, and
 * you cannot practise that against a set of scenarios where the answer is
 * always the same.
 *
 * So one of the five scenarios here is a plain single-station fault, the
 * correct call on it is "go to the tool and run its shift report", and a
 * student who has learned to reflexively blame the route gets it wrong.
 *
 * WHAT THE FREE READINGS LOOK LIKE
 *
 * All of them fine. That is not a design choice, it is the definition: every
 * per-visit check passes, every tool alarm is silent, and the MES summary is
 * green. The evidence that decides this lives in readings that cost hours -
 * plotting a quantity against layer number, cross-sectioning a junction,
 * re-measuring eleven layers on a golden wafer - because in a real fab those
 * are exactly the things nobody has run yet when the meeting starts.
 */

import { clamp } from '../../core/math'
import { CD_BUDGET_NM } from '../litho/topography'
import {
  ANNEAL_VISIT_SPEC_S,
  CMP_ROUTE_DERIVED_SPEC_NM,
  CMP_VISIT_SPEC_NM,
  NOMINAL_PLANARISATION_EFFICIENCY,
  driftAcrossLayers,

  planarisationStack,
  thermalAccount,
} from './routeFaults'
import { t, tOr } from '../../i18n/i18n'
import type { ConditionModel, ToolAspect, ToolFault, ToolReading } from '../faults/model'

export type RouteAspect = 'planarisation' | 'thermalBudget' | 'toolDrift' | 'singleStation'

export interface RouteCondition {
  /** Planarisation efficiency at CMP-11. Nominal 0.90. */
  planarisationEfficiency: number
  /** Well drive-in time [s]. Nominal 300, and legal up to 900 on its own. */
  driveInSeconds: number
  /** Per-layer drift of a tool measured once per layer. Nominal zero. */
  driftPerLayer: number
  /**
   * Yield lost to an ordinary single-station fault [percentage points].
   *
   * The honest fourth possibility. When this is what is wrong, the route
   * machinery is a distraction and the answer is to go and open the tool.
   */
  singleStationLossPts: number
}

export const NOMINAL_ROUTE_CONDITION: RouteCondition = {
  planarisationEfficiency: NOMINAL_PLANARISATION_EFFICIENCY,
  driveInSeconds: 300,
  driftPerLayer: 0,
  singleStationLossPts: 0,
}

/** Junction depth the flow is allowed [nm]. */
const JUNCTION_LIMIT_NM = 78
/** Specification half-width for the drifting measurement. */
const DRIFT_SPEC_HALF = 3
/** Baseline composite yield of a healthy route [%]. */
const BASE_YIELD_PCT = 92

// Step names surface in the route report's budget table, so they translate
// like every other label a student reads - key wins, English is the source.
const flowFor = (c: RouteCondition) => [
  { label: tOr('tool.route.step.wellDriveIn', 'Well drive-in'), seconds: c.driveInSeconds },
  { label: tOr('tool.route.step.gateOxidation', 'Gate oxidation'), seconds: 240 },
  { label: tOr('tool.route.step.spacerDensify', 'Spacer densify'), seconds: 120 },
  { label: tOr('tool.route.step.spikeAnneal', 'Spike anneal'), seconds: 60 },
]

export interface RouteOutcome {
  stack: ReturnType<typeof planarisationStack>
  budget: ReturnType<typeof thermalAccount>
  drift: ReturnType<typeof driftAcrossLayers>
  /** Composite yield at the end of the route [%]. */
  yieldPct: number
  /** Loss attributable to each mechanism [percentage points]. */
  lossFromTopography: number
  lossFromJunction: number
  lossFromDrift: number
  lossFromStation: number
}

/**
 * Run the whole route under a condition.
 *
 * Each mechanism costs yield through its own physics rather than through a
 * shared fudge: topography costs it at the scanner, an over-spent budget costs
 * it at the junction, a drift costs it by moving a centred process off centre,
 * and a station fault costs it directly. They add, because on a real route they
 * do.
 */
export function routeOutcome(c: RouteCondition): RouteOutcome {
  const stack = planarisationStack(10, c.planarisationEfficiency)
  const budget = thermalAccount(flowFor(c), 24, JUNCTION_LIMIT_NM)
  const drift = driftAcrossLayers({
    layers: 11,
    target: 0,
    driftPerLayer: c.driftPerLayer,
    noiseSigma: 0.35,
    controlSigma: 0.9,
    specHalfWidth: DRIFT_SPEC_HALF,
  })

  // Topography past the focus budget starts costing prints.
  const lossFromTopography = clamp((stack.cdSpreadNm / CD_BUDGET_NM - 1) * 4.5, 0, 40)
  // A junction past its limit costs parametric yield.
  const lossFromJunction = clamp((budget.finalJunctionNm / JUNCTION_LIMIT_NM - 1) * 90, 0, 40)
  // A process that is no longer centred loses the tail nearest the limit.
  const lossFromDrift = clamp((drift.totalDriftNm / DRIFT_SPEC_HALF) * 12, 0, 40)
  const lossFromStation = clamp(c.singleStationLossPts, 0, 40)

  return {
    stack,
    budget,
    drift,
    yieldPct: clamp(
      BASE_YIELD_PCT - lossFromTopography - lossFromJunction - lossFromDrift - lossFromStation,
      0,
      100,
    ),
    lossFromTopography,
    lossFromJunction,
    lossFromDrift,
    lossFromStation,
  }
}

const ROUTE_ASPECTS: ToolAspect<RouteAspect>[] = [
  {
    id: 'planarisation',
    label: 'Accumulation across the metal stack',
    action:
      'Dress the CMP-11 pad conditioner, and re-derive the per-visit step-height limit from the ' +
      'focus budget instead of from what the tool can hold',
    downtimeHours: 6,
    readingIds: ['cmpVisits', 'layerTrend'],
  },
  {
    id: 'thermalBudget',
    label: 'Thermal budget spent before the last anneal',
    action:
      'Put the well drive-in back to 300 s and find another way to meet the resistivity target',
    downtimeHours: 8,
    readingIds: ['thermalTotal', 'junction'],
  },
  {
    id: 'toolDrift',
    label: 'A tool drifting across the layer sequence',
    action: 'Find the drifting tool from the layer chart and re-centre it',
    downtimeHours: 5,
    readingIds: ['spcRules', 'goldenWafer'],
  },
  {
    id: 'singleStation',
    label: 'One station, and the ordinary diagnosis applies',
    action:
      'Go to the tool that is alarming and run its own shift report — this is not a route problem',
    downtimeHours: 4,
    readingIds: ['stationAlarms'],
  },
]

/**
 * The readings a yield meeting already has in front of it.
 *
 * Every one of them is green on a route fault, which is why the meeting is
 * happening at all.
 */
const routeReadings = (c: RouteCondition): ToolReading[] => {
  const o = routeOutcome(c)

  return [
    {
      id: 'cmpVisits',
      label: 'Post-CMP step height, every layer',
      value: t('tool.route.reading.cmpVisits.Value', { a: o.stack.visits.filter((v) => v.inSpec).length, b: o.stack.visits.length, c: CMP_VISIT_SPEC_NM }),
      limit: 'all 10',
      // Judged against the limit as WRITTEN, because that is the check the fab
      // actually runs. It passes. That is the problem.
      state: o.stack.everyVisitInSpec ? 'good' : 'critical',
    },
    {
      id: 'thermalTotal',
      label: 'Thermal budget spent this flow',
      value: `${o.budget.totalSeconds} s`,
      limit: `no step over ${ANNEAL_VISIT_SPEC_S} s`,
      state: o.budget.everyVisitInSpec ? 'good' : 'critical',
    },
    {
      id: 'spcRules',
      label: 'Control-chart rules fired across the layer sequence',
      value: o.drift.violations.length
        ? [...new Set(o.drift.violations.map((v) => v.rule))].join(', ')
        : tOr('tool.route.reading.spcRules.ValueNone', 'none'),
      limit: 'none',
      state: o.drift.patternDetected ? 'critical' : 'good',
    },
    {
      id: 'stationAlarms',
      label: 'Stations currently out of specification',
      value: c.singleStationLossPts > 0 ? tOr('tool.route.reading.stationAlarms.ValueCmp', 'CMP-11 removal rate low') : tOr('tool.route.reading.stationAlarms.ValueNone', 'none'),
      limit: 'none',
      state: c.singleStationLossPts > 0 ? 'critical' : 'good',
    },
  ]
}

/**
 * The readings that cost hours, and the reason this is a decision.
 *
 * Each one is a quantity plotted against LAYER rather than against a limit,
 * which is the move the whole module exists to teach - and each one takes a
 * shift's worth of tool time to get.
 */
const routeEarned = (c: RouteCondition): ToolReading[] => {
  const o = routeOutcome(c)
  const climb =
    o.stack.visits[o.stack.visits.length - 1].outgoingNm - o.stack.visits[0].outgoingNm
  return [
    {
      id: 'layerTrend',
      label: 'Plot post-CMP range against layer number (1.5 h)',
      value: `${o.stack.visits[0].outgoingNm.toFixed(0)} → ${o.stack.finalRangeNm.toFixed(0)} nm, settles at ${o.stack.steadyStateNm.toFixed(0)}`,
      limit: `flat, and under ${CMP_ROUTE_DERIVED_SPEC_NM} nm`,
      state: o.stack.everyVisitMeetsRoute ? 'good' : climb > 8 ? 'critical' : 'warning',
    },
    {
      id: 'junction',
      label: 'Cross-section the junction — destructive (4.0 h)',
      value: `${o.budget.finalJunctionNm.toFixed(0)} nm`,
      limit: `< ${JUNCTION_LIMIT_NM} nm`,
      state: o.budget.routeInSpec ? 'good' : 'critical',
    },
    {
      id: 'goldenWafer',
      label: 'Re-measure the last 11 layers on a golden wafer (6.0 h)',
      value: `${o.drift.totalDriftNm.toFixed(2)} of drift across the sequence`,
      limit: '< 0.5',
      state: Math.abs(o.drift.totalDriftNm) < 0.5 ? 'good' : 'critical',
    },
  ]
}

const ROUTE_FAULT_CASES: ToolFault<RouteAspect, RouteCondition>[] = [
  {
    id: 'routeHealthy',
    label: 'Nothing wrong',
    complaint: 'Yield review. The line is running to forecast.',
    truth: 'Nothing is wrong. The correct call is to keep running.',
    evidence: [
      'Every per-visit check passes and no station is alarming.',
      'The layer plots are flat and the junction is on target.',
    ],
    aspect: null,
    apply: (n) => ({ ...n }),
  },
  {
    id: 'stackAccumulation',
    label: 'Metal 8 will not print, and CMP-11 is in spec on every layer',
    complaint:
      'Litho is scrapping at metal 8 on every lot and blaming focus. CMP says it is in spec on ' +
      'all ten layers and has the data to prove it.',
    truth:
      'The CMP-11 pad conditioner is worn, so planarisation efficiency has fallen from 0.90 to ' +
      '0.55. Every polish still leaves less than the 80 nm per-visit limit — and it passes more ' +
      'topography upward than it used to, and ten layers of that compounds to 70 nm. Both parties ' +
      'are telling the truth. The per-visit limit was never derived from the focus budget, and ' +
      'the gap between 80 nm and the 55 nm the scanner needed is the room the fault hid in.',
    evidence: [
      'Post-CMP step height passes on all ten layers, against the limit as written.',
      'Plotted against LAYER NUMBER it climbs and settles high instead of staying flat.',
      'No station is alarming and the junction is on target.',
    ],
    aspect: 'planarisation',
    apply: (n) => ({ ...n, planarisationEfficiency: 0.55 }),
  },
  {
    id: 'budgetSpent',
    label: 'The junction is too deep and no anneal was too long',
    complaint:
      'Parametric bins have shifted across the whole product. Every furnace step is inside its ' +
      'own window and THRM-04 diagnoses clean at every visit.',
    truth:
      'The well drive-in was extended from 300 s to 880 s to fix a resistivity complaint. It is ' +
      'still a legal anneal — the per-step limit is 900 s — and it spent budget the spike anneal ' +
      'at the end of the flow was depending on. Junction depth goes as the square root of the ' +
      'TOTAL, so the last step in a flow is constrained by every step before it, and the account ' +
      'has no owner.',
    evidence: [
      'No individual anneal is over its limit.',
      'The junction cross-section is past 78 nm.',
      'The CMP layer plot is flat and no control-chart rule has fired.',
    ],
    aspect: 'thermalBudget',
    apply: (n) => ({ ...n, driveInSeconds: 880 }),
  },
  {
    id: 'sequenceDrift',
    label: 'Eleven measurements, all in spec, and something is moving',
    complaint:
      'Parametric yield is sliding a little every week and nobody can point at a lot where it ' +
      'went wrong. Nothing has alarmed.',
    truth:
      'A tool is drifting about 0.18 per layer. Over eleven layers that is 1.8 — sixty per cent ' +
      'of the one-sided specification — and not one reading has left its control limits, because ' +
      'the drift is small compared with the limits and large compared with the run-to-run scatter. ' +
      'Eight consecutive points on one side of the centreline is a one-in-128 event on a centred ' +
      'process, which is why the rule exists and why only a chart across visits has one.',
    evidence: [
      'Control-chart rules have fired without a single point outside a limit.',
      'The golden wafer shows the accumulated shift directly.',
      'CMP layer plot is flat and the junction is on target.',
    ],
    aspect: 'toolDrift',
    apply: (n) => ({ ...n, driftPerLayer: 0.18 }),
  },
  {
    id: 'justOneTool',
    label: 'It is one tool, and the route is a distraction',
    complaint:
      'Yield down five points since Tuesday. The yield engineer has opened the route review and ' +
      'wants to talk about accumulation.',
    truth:
      'It is not a route fault. CMP-11 is alarming on removal rate — an ordinary single-station ' +
      'problem with an ordinary diagnosis, and its own shift report will name it in twenty ' +
      'minutes. Most yield excursions ARE one tool, and a student who has learned to reflexively ' +
      'reach for the route machinery has learned the wrong half of the lesson. Read the station ' +
      'alarms first; they are free.',
    evidence: [
      'A station is out of specification, which on a genuine route fault never happens.',
      'The layer plot is flat, the junction is on target, and no control rule has fired.',
      'The change was abrupt and dated — route faults arrive gradually.',
    ],
    aspect: 'singleStation',
    apply: (n) => ({ ...n, singleStationLossPts: 5 }),
  },
]

export const ROUTE_CONDITION_MODEL: ConditionModel<RouteAspect, RouteCondition> = {
  // Not a tool on the floor - the whole line, diagnosed as one. It gets an id
  // anyway because its faults are read the same way every tool's are.
  toolId: 'route',
  toolLabel: 'The route — every station, in order',
  nominal: NOMINAL_ROUTE_CONDITION,
  aspects: ROUTE_ASPECTS,
  faults: ROUTE_FAULT_CASES,
  restore(condition, aspect) {
    const n = NOMINAL_ROUTE_CONDITION
    switch (aspect) {
      case 'planarisation':
        return { ...condition, planarisationEfficiency: n.planarisationEfficiency }
      case 'thermalBudget':
        return { ...condition, driveInSeconds: n.driveInSeconds }
      case 'toolDrift':
        return { ...condition, driftPerLayer: n.driftPerLayer }
      case 'singleStation':
        return { ...condition, singleStationLossPts: n.singleStationLossPts }
    }
  },
  readings: routeReadings,
  earnedReadings: routeEarned,
  evaluate(condition) {
    const o = routeOutcome(condition)
    // Composite yield is the primary because it is the only number the whole
    // route has, and CD spread is the secondary because it is the one axis a
    // single station cannot explain.
    return {
      primary: o.yieldPct,
      secondary: o.stack.cdSpreadNm,
      unit: '%',
    }
  },
  secondaryLabel: 'CD spread at the last layer',
  secondaryUnit: 'nm',
  secondaryScale: 4,
  primaryScale: 4,
  spec: { lo: 90, hi: 100, maxSecondary: CD_BUDGET_NM },
}
